import React, { useState, useRef, useEffect } from 'react'
import { noop, isNil } from 'lodash/fp'
import { useSelector, useDispatch } from 'react-redux'
import { Flex } from 'rebass'

import { getFileWithName, isPDFFileType, getErrorByTypeName } from 'e-submission/apps/selectors'
import {
  removeFile,
  deletePhoto,
  browsePhoto,
  capturePhoto,
  pickFromGallery,
  previewPhoto,
  removeError,
} from 'e-submission/apps/actions'

import styled from 'styled-components'
import { Button, ModalHeader, ModalBody, ModalFooter } from 'reactstrap'
import Dialog from 'e-submission/components/Dialog'
import Icon from 'e-submission/components/Icon'
import FormEngine from 'e-submission/components/FormEngine'
import waterMaskUrl from 'assets/images/water-mask.png'
import viewerUrl from 'file-loader?name=pdf-viewer/web/viewer.html&emitFile=false!e-submission/pdf-viewer/web/viewer.html'
import { isBlob } from 'e-submission/utils/pattern'
import { resolveImageFile } from 'e-submission/utils/promise'
import { getToggles } from 'quick-quote/feature-toggles'

export const WatermarkModal = styled(Dialog)`
  .modal-content {
    height: 100vh;
  }
  .modal-title {
    display: flex;
    flex: 1 0 100%;
    justify-content: space-between;
    align-items: center;
    button {
      padding: 5px 10px;
    }
  }
  .modal-body {
    display: flex;
    flex-direction: column;
    padding: 0 !important;
    iframe {
      flex: 1;
    }
  }
  .modal-footer {
    justify-content: space-between;
  }
  .formEngine {
    min-height: 260px;
  }
`
const onLoadIframe = (evt) => {
  const iframe = evt.target
  const iframeDocument = iframe.contentWindow.document
  const toolbar = iframeDocument.querySelector('.toolbar')
  const main = iframeDocument.querySelector('#mainContainer')
  const content = iframeDocument.querySelector('#viewerContainer')
  const customStyle = document.createElement('style')
  customStyle.type = 'text/css'
  customStyle.textContent = `
    .canvasWrapper:before {
      content: ' ';
      position: absolute;
      top: 0;
      left: 0;
      height: 100%;
      width: 100%;
      background-image: url(${waterMaskUrl});
      background-size: 200px;
      z-index: 10;
    }
  `
  iframeDocument.head.appendChild(customStyle)
  toolbar.style.visibility = 'hidden'
  content.style.top = 0
}

export const WithWatermark = styled(({ image, ...props }) => <Flex {...props} />)`
  position: relative;
  overflow: hidden;
  background-color: black;
  background-image: url('${({ image }) => image}');
  background-repeat: no-repeat;
  background-position: center;
  background-size: contain;
  &:before {
    content: ' ';
    position: absolute;
    top: 0;
    left: 0;
    height: 100%;
    width: 100%;
    background-image: url(${waterMaskUrl});
    background-size: 200px;
    background-attachment: fixed;
  }
`

const GalleryPhotoCard = ({
  onOpen: onOpenProp = noop,
  onOk: onOkProp = noop,
  isBrowseFile: isBrowseFileProp = window.cordova ? false : true,
  isUploadError: isUploadErrorProp = false,
  accept: acceptProp = 'image/x-png,image/jpeg,application/pdf',
  name,
  disabled,
  sections,
  checked,
  readOnly,
  CheckedComponent,
  NotCheckedComponent,
  validateFile = noop,
  isDisabledDeleteBtn,
  callServiceBeforeBrowse,
  editable = false,
  ...props
}) => {
  const ref = useRef(null)

  const [isOpen, setIsOpen] = useState(false)
  const [isActionSheetOpen, setIsActionSheetOpen] = useState(false)

  const url = useSelector(state => getFileWithName(state, name))
  const isPDF = useSelector(state => isPDFFileType(state, name))
  const isUploadError = useSelector(state => !isNil(getErrorByTypeName(state, name)))

  const dispatch = useDispatch()

  const clearFile = () => {
    if (ref.current) {
      ref.current.value = ''
    }
  }

  const openModal = () => setIsOpen(true)
  const closeModal = () => {
    clearFile()
    setIsOpen(false)
  }

  const openActionSheet = () => setIsActionSheetOpen(true)
  const closeActionSheet = () => setIsActionSheetOpen(false)

  const onOk = () => {
    const isOk = onOkProp()
    if (isOk !== false) {
      closeModal()
      if (!editable) {
        dispatch(removeFile(name))
      }
    }
  }

  const onClose = () => {
    closeModal()
    if (!editable) {
      dispatch(removeFile(name))
    }
  }

  useEffect(() => {
    if (isOpen && isUploadError) {
      onClose()
    }
  }, [isOpen, isUploadError, onClose])

  const onDelete = () => {
    closeModal()
    dispatch(deletePhoto(name))
    if (!editable) {
      dispatch(removeFile(name))
    }
  }

  const onPreview = () => {
    if (checked) {
      openModal()
      dispatch(previewPhoto(name))
    }
  }

  const onOpen = async (event) => {
    dispatch(removeError(name))
    onOpenProp()

    const isShowPreview = !isNil(sections)

    if (isShowPreview) {
      openModal()
    }

    if (isBrowseFileProp) {
      const files = Array.from(event.target.files) || []
      const file = event.target.files[0]

      if (isBlob(file) && files.length > 0) {
        const format = await resolveImageFile(file)
        validateFile()
        dispatch(browsePhoto(name, files[0], isShowPreview, undefined, format))
      } else {
        validateFile()
      }
    } else {
      validateFile()
      dispatch(capturePhoto(name, isShowPreview))
    }

    if (editable) {
      clearFile()
    }
  }

  const onCapturePhoto = () => {
    closeActionSheet()
    dispatch(removeError(name))
    onOpenProp()
    validateFile()
    const isShowPreview = !isNil(sections)
    if (isShowPreview) openModal()
    dispatch(capturePhoto(name, isShowPreview))
  }

  const onPickFromGallery = () => {
    closeActionSheet()
    dispatch(removeError(name))
    onOpenProp()
    validateFile()
    const isShowPreview = !isNil(sections)
    if (isShowPreview) openModal()
    dispatch(pickFromGallery(name, isShowPreview))
  }

  const beforeBrowseFile = (callback) => {
    if (getToggles().ENABLE_QR_PAYMENT_STATUS && callServiceBeforeBrowse) {
      dispatch({
        type: callServiceBeforeBrowse,
        payload: {
          isOnlyCheckQr: true,
          callbackCheckingQr: callback,
        },
      })
      return
    }
    callback()
  }

  const browseFile = () =>
    beforeBrowseFile(() => {
      ref.current.click()
    })

  const handleNotCheckedClick = () => {
    if (disabled) return
    if (window.cordova) {
      openActionSheet()
    } else {
      browseFile()
    }
  }

  return (
    <div>
      <WatermarkModal isOpen={isOpen} toggle={onClose}>
        <ModalHeader tag="div">
          <div className="btn-left" onClick={onClose}>
            <Icon.arrowLeft className="icon" />
          </div>
          <div>{props.description}</div>
          <div />
        </ModalHeader>
        <ModalBody>
          {!isPDF ? (
            <WithWatermark flex="1 1 100%" align="center" image={url} />
          ) : (
            <iframe src={`${viewerUrl}?file=${url}`} onLoad={onLoadIframe} />
          )}
          {sections && (
            <div className="formEngine">
              <FormEngine sections={sections} />
            </div>
          )}
        </ModalBody>
        {readOnly ? null : (
          <ModalFooter>
            <Button
              color={isDisabledDeleteBtn ? 'disabled' : 'primary'}
              outline
              onClick={onDelete}
              disabled={isDisabledDeleteBtn}
            >
              ลบ
            </Button>
            <Button color={!checked ? 'disabled' : 'primary'} onClick={onOk}>
              ตกลง
            </Button>
          </ModalFooter>
        )}
      </WatermarkModal>

      <Dialog isOpen={isActionSheetOpen} toggle={closeActionSheet}>
        <ModalHeader tag="div">
          <div>{`เลือกวิธีแนบ${props.description}`}</div>
          <div className="btn-left" onClick={closeActionSheet}>
            <Icon.arrowLeft className="icon" />
          </div>
        </ModalHeader>
        <ModalBody>
          <div style={{ padding: '16px 0 16px 20px', cursor: 'pointer' }} onClick={onCapturePhoto}>
            ถ่ายรูป
          </div>
          <div style={{ padding: '16px 0 16px 20px', cursor: 'pointer' }} onClick={onPickFromGallery}>
            เลือกรูปจาก Gallery
          </div>
        </ModalBody>
      </Dialog>

      {checked ? (
        <CheckedComponent {...props} onClick={onPreview} />
      ) : (
        <div>
          <NotCheckedComponent
            {...props}
            validateFile={validateFile}
            disabled={disabled}
            onClick={handleNotCheckedClick}
          />
          <input
            ref={ref}
            type="file"
            onChange={onOpen}
            onClick={(e) => {
              e.stopPropagation()
            }}
            accept={acceptProp}
            style={{ display: 'none' }}
          />
        </div>
      )}
    </div>
  )
}

export default GalleryPhotoCard
